import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FiChevronDown, FiCheck } from 'react-icons/fi';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md';
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Wybierz...',
  className = '',
  size = 'md',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 160),
        });
      }
      return !prev;
    });
  }, []);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const dropdown = isOpen
    ? ReactDOM.createPortal(
        <div
          className="custom-select__dropdown"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            minWidth: dropdownPos.width,
            zIndex: 9999,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="custom-select__options">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`custom-select__option ${option.value === value ? 'custom-select__option--selected' : ''}`}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>
                {option.value === value && <FiCheck size={14} className="custom-select__check" />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={ref}
      className={`custom-select ${isOpen ? 'open' : ''} ${size === 'sm' ? 'custom-select--sm' : ''} ${className}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="custom-select__trigger"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <span className={`custom-select__label ${!selectedOption ? 'custom-select__label--placeholder' : ''}`}>
          {displayLabel}
        </span>
        <FiChevronDown className={`custom-select__chevron ${isOpen ? 'custom-select__chevron--open' : ''}`} size={14} />
      </button>
      {dropdown}
    </div>
  );
};

export default Select;
